import { useState } from 'react'
import { CheckCircle2, ChevronDown, ChevronRight, MinusCircle, XCircle } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useTestRunsByTestCase } from '@/hooks'
import { TestRunStatuses } from '@/types'
import type { TestRun } from '@/types'
import { cn } from '@/lib/utils'

export const TEST_RUNS_PAGE = { maxResultCount: 50, skipCount: 0 } as const

interface TestCaseRunsTabProps {
  projectId: string
  testCaseId: string
}

export function TestCaseRunsTab({ projectId, testCaseId }: TestCaseRunsTabProps) {
  const { data, isLoading, isError } = useTestRunsByTestCase(projectId, testCaseId, TEST_RUNS_PAGE)

  if (isLoading) {
    return (
      <div className="space-y-3" role="status" aria-live="polite" aria-label="Loading test runs" data-testid="test-runs-loading">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    )
  }

  if (isError) {
    return (
      <Card className="p-6 text-center text-sm text-destructive" role="alert" data-testid="test-runs-error">
        <p className="font-medium mb-1">Could not load test runs</p>
        <p>Refresh the page or try again later.</p>
      </Card>
    )
  }

  if (!data || data.items.length === 0) {
    return (
      <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="test-runs-empty">
        <p className="font-medium text-foreground mb-1">No runs yet</p>
        <p>This test case has no recorded runs. Trigger the Playwright workflow to produce the first run.</p>
      </Card>
    )
  }

  return (
    <div className="space-y-3" data-testid="test-runs-list">
      <p className="text-xs text-muted-foreground">{data.totalCount} run{data.totalCount === 1 ? '' : 's'} recorded</p>
      <Card className="overflow-hidden">
        <table className="w-full table-fixed break-words text-sm" aria-label="Test run history">
          <thead className="bg-muted/40">
            <tr>
              <th className="px-3 py-2 text-left font-medium w-[48px]" />
              <th className="px-3 py-2 text-left font-medium w-[130px]">When</th>
              <th className="px-3 py-2 text-left font-medium w-[100px]">Result</th>
              <th className="px-3 py-2 text-left font-medium w-[90px]">Duration</th>
              <th className="px-3 py-2 text-left font-medium w-[120px]">Source</th>
              <th className="px-3 py-2 text-left font-medium">Failed at</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map(run => (
              <RunRow key={run.id} run={run} />
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}

function RunRow({ run }: { run: TestRun }) {
  const [expanded, setExpanded] = useState(false)
  const failed = run.status === TestRunStatuses.Failed
  const expandable = failed

  return (
    <>
      <tr
        className={cn('border-t', expandable && 'cursor-pointer hover:bg-muted/30')}
        onClick={() => expandable && setExpanded(v => !v)}
        aria-expanded={expandable ? expanded : undefined}
        data-testid={`test-run-row-${run.id}`}
      >
        <td className="px-3 py-3">
          {expandable && (expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)}
        </td>
        <td className="px-3 py-3 text-xs text-muted-foreground">
          {new Date(run.runAt).toLocaleString()}
        </td>
        <td className="px-3 py-3">
          <ResultBadge status={run.status} />
        </td>
        <td className="px-3 py-3 text-xs text-muted-foreground">
          {`${(run.durationMs / 1000).toFixed(2)}s`}
        </td>
        <td className="px-3 py-3 text-xs text-muted-foreground">
          {<>{run.executionSource}{run.sourceRef && <span className="ml-1 opacity-70">· {run.sourceRef.substring(0, 8)}</span>}</>}
        </td>
        <td className="px-3 py-3 text-xs text-muted-foreground">
          {failed && run.failedStepName
              ? (run.failedStepIndex !== null && run.failedStepIndex !== undefined
                ? `step ${run.failedStepIndex + 1}: ${run.failedStepName}`
                : run.failedStepName)
              : '—'}
        </td>
      </tr>
      {expanded && failed && (
        <tr className="border-t bg-muted/20">
          <td colSpan={6} className="px-6 py-4">
            <div className="space-y-2">
              {run.errorMessage && (
                <div>
                  <p className="text-xs font-semibold mb-1">Error</p>
                  <pre className="text-xs whitespace-pre-wrap break-words rounded bg-background p-3 border">
                    {run.errorMessage}
                  </pre>
                </div>
              )}
              {run.errorStack && (
                <details className="text-xs">
                  <summary className="cursor-pointer font-semibold mb-1">Stack trace</summary>
                  <pre className="whitespace-pre-wrap break-words rounded bg-background p-3 border mt-1">
                    {run.errorStack}
                  </pre>
                </details>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

const RESULT_BADGE_BY_STATUS: Record<string, { styles: string; Icon: typeof CheckCircle2 }> = {
  [TestRunStatuses.Passed]: {
    styles: 'text-success-foreground bg-success border-success-foreground',
    Icon: CheckCircle2,
  },
  [TestRunStatuses.Failed]: {
    styles: 'text-danger-foreground bg-danger border-danger-foreground',
    Icon: XCircle,
  },
}

const DEFAULT_RESULT_BADGE = {
  styles: 'text-warning-foreground bg-warning border-warning-foreground',
  Icon: MinusCircle,
}

function ResultBadge({ status }: Readonly<{ status: string }>) {
  const { styles, Icon } = RESULT_BADGE_BY_STATUS[status] ?? DEFAULT_RESULT_BADGE

  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs', styles)}>
      <Icon className="h-3 w-3" />
      {status}
    </span>
  )
}
